import { createFileRoute, notFound, redirect } from '@tanstack/react-router'
import type { ReactElement } from 'react'

import { assertDefinedAndNotNull } from '@orus.eu/error'

import {
  pickPropagatedSearchParams,
  SUBSCRIPTION_FUNNEL_PROPAGATED_KEYS,
} from '#/lib/subscription-injectable-params.ts'
import type { StandaloneFormAccess } from '#/modules/standalone-form/front-office/standalone-form-page/standalone-form-access.types.ts'
import { StandaloneFormPageSkeleton } from '#/modules/standalone-form/front-office/standalone-form-page/standalone-form-page-skeleton.tsx'
import { StandaloneFormPage } from '#/modules/standalone-form/front-office/standalone-form-page/standalone-form-page.tsx'
import { findStandaloneForm } from '#/modules/standalone-form/front-office/standalone-form-page/standalone-form-registry.ts'
import { standaloneFormSearchSchema } from '#/modules/standalone-form/front-office/standalone-form-page/standalone-form-search.utils.ts'

export const Route = createFileRoute('/form/$subscriptionId/$formId')({
  validateSearch: standaloneFormSearchSchema,
  beforeLoad: ({ params }) => {
    if (!findStandaloneForm(params.formId)) throw notFound()
  },
  loader: async function loadStandaloneForm({ context, params }) {
    const registration = assertDefinedAndNotNull(
      findStandaloneForm(params.formId),
      'While loading the standalone form page, found no form registered under the id beforeLoad accepted',
      { formId: params.formId },
    )
    const access = await registration.ensureFormAccess(context, params.subscriptionId)
    switch (access.type) {
      case 'allowed':
        return
      case 'not-available':
        throw notAvailableFormRedirect(access, params.subscriptionId)
    }
  },
  pendingComponent: StandaloneFormPageSkeleton,
  pendingMs: 150,
  component: StandaloneFormRoute,
})

function StandaloneFormRoute(): ReactElement {
  const { subscriptionId, formId } = Route.useParams()
  const { return: returnTo } = Route.useSearch()
  return <StandaloneFormPage subscriptionId={subscriptionId} formId={formId} returnTo={returnTo} />
}

function notAvailableFormRedirect(
  access: Extract<StandaloneFormAccess, { type: 'not-available' }>,
  subscriptionId: string,
) {
  switch (access.redirect.to) {
    case 'funnel':
      return redirect({
        to: '/subscribe/$subscriptionId/$stepId',
        params: { subscriptionId, stepId: access.redirect.stepId },
        search: (previous) => pickPropagatedSearchParams(previous, SUBSCRIPTION_FUNNEL_PROPAGATED_KEYS),
        replace: true,
      })
    case 'customer-portal':
      return redirect({ to: '/', replace: true })
  }
}
